/**
 * Filtering the GBL meta.
 *
 * Kept out of the page because the filters are the feature: "show me the Great
 * League steel types from Sinnoh in tier A" is the question the Battle Hub
 * exists to answer, and the only way to be sure it answers it correctly is to
 * be able to ask it without rendering anything.
 *
 * The data itself is generated from PvPoke — see battleMeta.generated.ts, which
 * carries the licence and the attribution the UI has to show.
 */
import {
  BATTLE_META,
  BATTLE_META_MEASURES,
  type BattleMetaEntry,
  type BattleMetaLeague,
  type BattleMetaMeasure,
} from "./generated/battleMeta.generated";
import { regionForId, type Region } from "./types";

export type { BattleMetaEntry, BattleMetaLeague, BattleMetaMeasure };
export { BATTLE_META_MEASURES };

/** The default measure: the list you want before deciding what it is FOR. */
export const DEFAULT_MEASURE = "overall";

export interface MetaFilters {
  /** A PvPoke type slug ("steel"), or "all". */
  type: string;
  /** A Region key, or "all". */
  region: string;
  /** An S/A/B/C band, or "all". */
  tier: string;
  /** True to keep only the SHADOW rows, which are a distinct pick. */
  shadowOnly?: boolean;
}

export const NO_FILTERS: MetaFilters = { type: "all", region: "all", tier: "all" };

/** The league a key names, or the first one when the key is unknown. */
export function leagueByKey(key: string): BattleMetaLeague {
  return BATTLE_META.find((l) => l.key === key) ?? BATTLE_META[0];
}

/** The measure a key names, or `overall` when the key is unknown. */
export function measureByKey(key: string): BattleMetaMeasure {
  return (
    BATTLE_META_MEASURES.find((m) => m.key === key) ??
    BATTLE_META_MEASURES[0]
  );
}

/** The score band, the same way the generator derived it for `overall`. */
function tierFor(score: number): string {
  // Mirrors the generator's bands. Kept in step by battleMeta.test.ts, which
  // re-derives every stored `tier` through this function.
  if (score >= 90) return "S";
  if (score >= 80) return "A";
  if (score >= 70) return "B";
  return "C";
}

/**
 * One measure's ranking, as ordinary entries the rest of this file can filter.
 *
 * The generated file does NOT repeat a species' metadata per measure — six
 * copies of every name, type and dex id would quadruple the bundle for data
 * that never differs. So a role ranking is stored as score + moveset rows, and
 * this resolves them against the overall list, falling back to the `species`
 * dictionary for the role specialists that never made the overall top 100.
 * There are 152 of those in Great League alone: a measure that silently
 * dropped them would be the overall list re-sorted, which is exactly what
 * these rankings are NOT.
 *
 * MOVES COME FROM THE SPECIES. PvPoke publishes the same recommended moveset
 * in every category, so the score is the only thing a role ranking changes.
 */
export function entriesForMeasure(
  league: BattleMetaLeague,
  measureKey: string,
): BattleMetaEntry[] {
  if (measureKey === DEFAULT_MEASURE) return [...league.entries];
  const rows = league.measures?.[measureKey];
  // An unknown measure falls back to overall rather than rendering nothing:
  // a stale bookmark should not be able to empty the page.
  if (!rows || rows.length === 0) return [...league.entries];

  const byId = new Map(league.entries.map((e) => [e.speciesId, e]));
  const out: BattleMetaEntry[] = [];
  for (const row of rows) {
    const known = byId.get(row.speciesId);
    const meta = known ?? league.species?.[row.speciesId];
    if (!meta) continue;
    out.push({
      rank: out.length + 1,
      dexId: meta.dexId,
      speciesId: meta.speciesId,
      name: meta.name,
      types: meta.types,
      shadow: meta.shadow,
      score: row.score,
      tier: tierFor(row.score),
      moves: meta.moves,
    });
  }
  return out;
}

/**
 * Apply the filters, keeping rank order.
 *
 * Rank is the point of a ranked list, so filtering NEVER reorders: an entry
 * keeps the number it earned in the full league. A filtered list that renumbers
 * itself 1..n would claim the top steel type is the best Pokémon in the league.
 */
export function filterMeta(
  entries: readonly BattleMetaEntry[],
  filters: MetaFilters,
): BattleMetaEntry[] {
  return entries.filter((e) => {
    if (filters.type !== "all" && !e.types.includes(filters.type)) return false;
    if (filters.tier !== "all" && e.tier !== filters.tier) return false;
    if (filters.shadowOnly && !e.shadow) return false;
    if (filters.region !== "all" && regionForId(e.dexId) !== (filters.region as Region)) {
      return false;
    }
    return true;
  });
}

/**
 * The types actually present in a league, so the filter row offers no dead end.
 *
 * Ordered by how many entries carry them: a league's defining types come first,
 * which is a useful read in itself before anything is even tapped.
 */
export function typesInLeague(entries: readonly BattleMetaEntry[]): string[] {
  const counts = new Map<string, number>();
  for (const e of entries) for (const t of e.types) counts.set(t, (counts.get(t) ?? 0) + 1);
  return [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])).map(([t]) => t);
}

/** The regions present in a league, in national-dex order. */
export function regionsInLeague(entries: readonly BattleMetaEntry[]): Region[] {
  const seen = new Set<Region>();
  for (const e of [...entries].sort((a, b) => a.dexId - b.dexId)) {
    const r = regionForId(e.dexId);
    if (r) seen.add(r);
  }
  return [...seen];
}

/** The tier bands present, strongest first. */
export function tiersInLeague(entries: readonly BattleMetaEntry[]): string[] {
  const order = ["S", "A", "B", "C"];
  const present = new Set(entries.map((e) => e.tier));
  return order.filter((t) => present.has(t));
}

/** Where an entry sits within one of its own types. */
export interface TypeRank {
  type: string;
  /** 1-based position among that type in this league. */
  rank: number;
  /** How many of that type are in the league at all, so #1 of 2 is readable. */
  outOf: number;
}

/**
 * Each entry's rank WITHIN each of its types.
 *
 * The league rank says how good a Pokémon is. It does not say what it is FOR,
 * and that is usually the decision: a dual-type sitting at #12 overall might be
 * the best Steel in the league and only the sixth-best Ice, which is the
 * difference between "build this" and "you already have better". A single
 * number cannot carry that, and renumbering the list under a filter would throw
 * away the overall rank to say it.
 *
 * Computed over the WHOLE league, so a badge means the same thing whatever
 * filter is on screen. "Of the league" is the top N the file carries, which is
 * where the meta lives, and the outOf makes the pool visible rather than
 * implied.
 */
export function typeRanksByEntry(
  entries: readonly BattleMetaEntry[],
): Map<string, TypeRank[]> {
  const totals = new Map<string, number>();
  for (const e of entries) for (const t of e.types) totals.set(t, (totals.get(t) ?? 0) + 1);

  const seen = new Map<string, number>();
  const out = new Map<string, TypeRank[]>();
  // `entries` is already strongest-first, so a running count IS the rank.
  for (const e of entries) {
    const ranks: TypeRank[] = [];
    for (const t of e.types) {
      const rank = (seen.get(t) ?? 0) + 1;
      seen.set(t, rank);
      ranks.push({ type: t, rank, outOf: totals.get(t) ?? rank });
    }
    out.set(e.speciesId, ranks);
  }
  return out;
}

/**
 * The filtered list, each row numbered within the filter as well.
 *
 * Both numbers are kept deliberately. `rank` stays the league rank it earned —
 * renumbering would claim the best Steel is the best Pokémon in the league —
 * and `filterRank` answers the question the filter just asked: of the things I
 * am looking at, where does this one come?
 */
export function rankWithinFilter(
  entries: readonly BattleMetaEntry[],
  filters: MetaFilters,
): Array<BattleMetaEntry & { filterRank: number; filterOutOf: number }> {
  const shown = filterMeta(entries, filters);
  return shown.map((e, i) => ({ ...e, filterRank: i + 1, filterOutOf: shown.length }));
}
